import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Lock, CheckCircle, XCircle, UserPlus, Check, X, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const PASSWORD_RULES = [
  { id: 'length', label: '12 caracteres como minimo', test: (p) => p.length >= 12 },
  { id: 'upper', label: 'Una letra mayuscula', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'Una letra minuscula', test: (p) => /[a-z]/.test(p) },
  { id: 'number', label: 'Un numero', test: (p) => /[0-9]/.test(p) },
  { id: 'special', label: 'Un simbolo especial (!@#$%...)', test: (p) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p) },
];

function ActivateAccount() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [validando, setValidando] = useState(true);
  const [tokenValido, setTokenValido] = useState(false);
  const [email, setEmail] = useState('');
  const [completado, setCompletado] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    validarToken();
  }, [token]);

  const validarToken = async () => {
    try {
      const response = await axios.get(`/api/auth/validate-token/${token}?tipo=invitation`);
      setTokenValido(response.data.valid);
      setEmail(response.data.email || '');
    } catch (err) {
      setTokenValido(false);
    } finally {
      setValidando(false);
    }
  };

  const allRulesPass = PASSWORD_RULES.every((r) => r.test(password));
  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const canSubmit = allRulesPass && passwordsMatch && !loading;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!allRulesPass) {
      setError('La contrasena no cumple todos los requisitos');
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contrasenas no coinciden');
      return;
    }

    setLoading(true);

    try {
      await axios.post('/api/auth/activate', { token, password });
      setCompletado(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Error al activar la cuenta');
    } finally {
      setLoading(false);
    }
  };

  if (validando) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Validando invitacion...</p>
        </div>
      </div>
    );
  }

  if (!tokenValido) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Invitacion invalida o expirada
          </h1>
          <p className="text-gray-600 mb-6">
            Este enlace de invitacion ya no es valido. Contacta con el administrador para que te envie una nueva invitacion.
          </p>
          <Link
            to="/"
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700"
          >
            Ir al login
          </Link>
        </div>
      </div>
    );
  }

  if (completado) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Cuenta activada
          </h1>
          <p className="text-gray-600 mb-6">
            Tu cuenta ha sido activada correctamente. Ya puedes iniciar sesion.
          </p>
          <button
            onClick={() => navigate('/')}
            className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700"
          >
            Ir al login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-8">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <UserPlus className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">
            Activa tu cuenta
          </h1>
          <p className="text-gray-600 mt-2">
            Crea una contrasena para <strong>{email}</strong>
          </p>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contrasena
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full pl-10 pr-10 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Crea tu contrasena"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Requisitos de contrasena - visibles siempre */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-1.5">
            <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Requisitos de la contrasena</p>
            {PASSWORD_RULES.map((rule) => {
              const passes = rule.test(password);
              const attempted = password.length > 0;
              return (
                <div key={rule.id} className="flex items-center gap-2 text-sm">
                  {attempted ? (
                    passes ? (
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-red-400 flex-shrink-0" />
                    )
                  ) : (
                    <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                  )}
                  <span className={attempted ? (passes ? 'text-green-700' : 'text-red-500') : 'text-gray-500'}>
                    {rule.label}
                  </span>
                </div>
              );
            })}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Confirmar contrasena
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className={`w-full pl-10 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                  confirmPassword
                    ? passwordsMatch
                      ? 'border-green-400 bg-green-50'
                      : 'border-red-300 bg-red-50'
                    : 'border-gray-300'
                }`}
                placeholder="Repite la contrasena"
                required
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                tabIndex={-1}
              >
                {showConfirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="text-red-500 text-xs mt-1">Las contrasenas no coinciden</p>
            )}
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed mt-2"
          >
            {loading ? 'Activando...' : 'Activar cuenta'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default ActivateAccount;
